const API_BASE = '/api';

function getToken() {
  return localStorage.getItem('crm_token');
}

async function upload(url, formData) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (res.status === 401) {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    window.location.href = '/login';
    // Lança em vez de devolver undefined: `window.location.href` é assíncrono,
    // o React segue renderizando, e uma promessa CUMPRIDA com undefined passava
    // pelos guards do chamador (`allSettled` → status 'fulfilled') até estourar
    // num `.length`. Requisição negada tem que parecer negada.
    // Ver Sentry CRM-TOTVS-MODA-CLIENT-K. O main.jsx descarta esse erro no
    // beforeSend — sessão expirada é fluxo normal, não defeito.
    const err = new Error('Sessão expirada');
    err.status = 401;
    err.sessionExpired = true;
    throw err;
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || 'Upload failed');
  }
  return res.json();
}

async function download(url) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    window.location.href = '/login';
    // Lança em vez de devolver undefined: `window.location.href` é assíncrono,
    // o React segue renderizando, e uma promessa CUMPRIDA com undefined passava
    // pelos guards do chamador (`allSettled` → status 'fulfilled') até estourar
    // num `.length`. Requisição negada tem que parecer negada.
    // Ver Sentry CRM-TOTVS-MODA-CLIENT-K. O main.jsx descarta esse erro no
    // beforeSend — sessão expirada é fluxo normal, não defeito.
    const err = new Error('Sessão expirada');
    err.status = 401;
    err.sessionExpired = true;
    throw err;
  }
  if (!res.ok) throw new Error(`Download falhou: ${res.statusText}`);

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'export.xlsx';

  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

async function request(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${url}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });

  if (res.status === 401) {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    window.location.href = '/login';
    // Lança em vez de devolver undefined: `window.location.href` é assíncrono,
    // o React segue renderizando, e uma promessa CUMPRIDA com undefined passava
    // pelos guards do chamador (`allSettled` → status 'fulfilled') até estourar
    // num `.length`. Requisição negada tem que parecer negada.
    // Ver Sentry CRM-TOTVS-MODA-CLIENT-K. O main.jsx descarta esse erro no
    // beforeSend — sessão expirada é fluxo normal, não defeito.
    const err = new Error('Sessão expirada');
    err.status = 401;
    err.sessionExpired = true;
    throw err;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.error || 'Request failed');
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

export const api = {
  // Auth
  login: (username, password) =>
    fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }).then(r => r.json()),

  logout: () =>
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).then(r => r.json()),

  verify2fa: (temp_token, code) =>
    fetch(`${API_BASE}/auth/2fa/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ temp_token, code }),
    }).then(r => r.json()),

  // 2FA management
  get2faStatus: () => request('/auth/2fa/status'),
  setup2fa: () => request('/auth/2fa/setup', { method: 'POST', body: JSON.stringify({}) }),
  enable2fa: (code) => request('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }),
  disable2fa: (code) => request('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) }),
  forcePasswordReset: (user_id) => request('/auth/force-reset', { method: 'POST', body: JSON.stringify({ user_id }) }),

  getMe: () => request('/users/me'),
  changePassword: (current_password, new_password) =>
    request('/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password, new_password }),
    }),
  getMyNotifications: () => request('/users/me/notifications'),
  saveMyNotifications: (data) =>
    request('/users/me/notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Leads
  getLeads: (params) => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const qs = new URLSearchParams(clean).toString();
    return request(`/leads?${qs}`);
  },
  getLead: (id) => request(`/leads/${id}`),
  getLeadsSummary: (funnel) => request(`/leads/summary?funnel=${funnel}`),
  createLead: (data) => request('/leads', { method: 'POST', body: JSON.stringify(data) }),
  updateLead: (id, data) => request(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLead: (id) => request(`/leads/${id}`, { method: 'DELETE' }),
  changeLeadFunnel: (id, funnel_type) =>
    request(`/leads/${id}/funnel`, { method: 'PATCH', body: JSON.stringify({ funnel_type }) }),

  // Team (canonical — replaces /api/esns for new code)
  getTeam: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/team${qs ? '?' + qs : ''}`);
  },
  getTeamMember:   (id) => request(`/team/${id}`),
  toggleTeamActive: (id) => request(`/team/${id}/toggle-active`, { method: 'POST' }),
  updateTeamMember: (id, data) => request(`/team/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // ESNs — DEPRECATED: redireciona para /api/team (mantido por compat com FunnelPage, LeadFormModal etc.)
  getEsns: () => request('/team?role=esn&active=true'),
  // ZYN-324: dropdown de escopo individual do manager precisa listar ESNs inativos também
  getAllEsns: () => request('/team?role=esn'),
  createEsn: (nome) => request('/esns', { method: 'POST', body: JSON.stringify({ nome }) }),
  updateEsn: (id, data) => request(`/esns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEsn: (id) => request(`/esns/${id}`, { method: 'DELETE' }),
  getEsnPerformance: () => request('/esns/performance'),

  // ESN Targets (Goals)
  getEsnTargets: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/esn-targets?${qs}`);
  },
  getEsnTargetAchievementSummary: () => request('/esn-targets/achievement-summary'),
  createEsnTarget: (data) => request('/esn-targets', { method: 'POST', body: JSON.stringify(data) }),
  updateEsnTarget: (id, data) => request(`/esn-targets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEsnTarget: (id) => request(`/esn-targets/${id}`, { method: 'DELETE' }),

  // Lead Origins
  getLeadOrigins: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/lead-origins${q ? '?' + q : ''}`);
  },
  createLeadOrigin: (data) => request('/lead-origins', { method: 'POST', body: JSON.stringify(data) }),
  updateLeadOrigin: (id, data) => request(`/lead-origins/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLeadOrigin: (id) => request(`/lead-origins/${id}`, { method: 'DELETE' }),

  // Reports de origem
  getLeadsByOriginReport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/reports/leads-by-origin${q ? '?' + q : ''}`);
  },
  getWonByOriginReport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/reports/won-by-origin${q ? '?' + q : ''}`);
  },

  // Products
  getProducts: () => request('/products'),
  createProduct: (nome) => request('/products', { method: 'POST', body: JSON.stringify({ nome }) }),
  updateProduct: (id, nome) => request(`/products/${id}`, { method: 'PUT', body: JSON.stringify({ nome }) }),
  toggleProduct: (id) => request(`/products/${id}/toggle`, { method: 'PATCH' }),
  deleteProduct: (id) => request(`/products/${id}`, { method: 'DELETE' }),

  // Contas
  getContas: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/contas?${qs}`);
  },
  getConta: (id) => request(`/contas/${id}`),
  getContaById: (id) => request(`/contas/${id}`),
  enrichConta: (id) => request(`/contas/${id}/enrich`, { method: 'POST' }),
  createConta: (data) => request('/contas', { method: 'POST', body: JSON.stringify(data) }),
  updateConta: (id, data) => request(`/contas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteConta: (id, { force = false } = {}) => request(`/contas/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),
  updateContaStatus: (id, data) => request(`/contas/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  getContaFiliais: (id) => request(`/contas/${id}/filiais`),
  getContaOportunidades: (id) => request(`/contas/${id}/oportunidades`),
  getContaInteractions: (id) => request(`/contas/${id}/interactions`),
  getContaTimeline: (id, cursor, limit = 30) => {
    const qs = new URLSearchParams({ limit, ...(cursor ? { cursor } : {}) }).toString();
    return request(`/contas/${id}/timeline?${qs}`);
  },

  // Contatos de conta
  getContatosConta: (contaId) => request(`/contatos-conta?conta_id=${contaId}`),
  createContatoConta: (data) => request('/contatos-conta', { method: 'POST', body: JSON.stringify(data) }),
  updateContatoConta: (id, data) => request(`/contatos-conta/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContatoConta: (id) => request(`/contatos-conta/${id}`, { method: 'DELETE' }),

  // Funnels
  getFunnels: () => request('/funnels'),

  // Dashboard
  getDashboard: (period) => request(`/dashboard${period ? '?period=' + period : ''}`),
  getDashboardConversion: (funnel, period) => request(`/dashboard/conversion?funnel=${funnel}&period=${period || '90d'}`),
  getEsnRanking: (period) => request(`/dashboard/esn-ranking?period=${period || '30d'}`),
  getEsnHeatmap: (year)   => request(`/dashboard/esn-heatmap?year=${year || new Date().getFullYear()}`),

  // Forecast
  getForecast: (year) => request(`/forecast?year=${year}`),
  // ZYN-323/324: painel Metas & Realizado — scope: 'unidade'|'esns'|'esn'
  getMetasRealizado: ({ year, scope, esnId } = {}) => {
    const params = new URLSearchParams({ year: year || new Date().getFullYear() });
    if (scope) params.set('scope', scope);
    if (esnId != null) params.set('esn_id', esnId);
    return request(`/metas-realizado?${params.toString()}`);
  },
  getForecastWeighted: (months = 3) => request(`/forecast/weighted?months=${months}`),
  getForecastDrilldown: (params) => request(`/forecast/drilldown?${new URLSearchParams(params)}`),
  getDashboardWinLoss: (params) => { const qs = new URLSearchParams(params).toString(); return request(`/dashboard/winloss?${qs}`); },
  getStageVelocity: (params) => { const qs = new URLSearchParams(params).toString(); return request(`/dashboard/stage-velocity?${qs}`); },

  // Lead Timeline
  getLeadTimeline: (leadId, cursor, limit = 30) => {
    const qs = new URLSearchParams({ limit, ...(cursor ? { cursor } : {}) }).toString();
    return request(`/leads/${leadId}/timeline?${qs}`);
  },
  createLeadComment: (leadId, content, mentioned_users = []) =>
    request(`/leads/${leadId}/comments`, { method: 'POST', body: JSON.stringify({ content, mentioned_users }) }),

  // Bulk actions
  bulkUpdateLeads: (ids, action, value) =>
    request('/leads/bulk', { method: 'PATCH', body: JSON.stringify({ ids, action, value }) }),

  // Sync (ERP Integration)
  getSyncStatus:  ()     => request('/sync/status'),
  getSyncConfig:  ()     => request('/sync/config'),
  saveSyncConfig: (data) => request('/sync/config', { method: 'PUT', body: JSON.stringify(data) }),
  runSync:        ()     => request('/sync/run', { method: 'POST' }),
  getSyncLog:     ()     => request('/sync/log?limit=30'),

  // Export
  downloadLeads: (params) => {
    const qs = new URLSearchParams(params).toString();
    return download(`/export/leads${qs ? '?' + qs : ''}`);
  },
  downloadLeadsCsv: (params) => {
    const qs = new URLSearchParams({ ...params, format: 'csv' }).toString();
    return download(`/export/leads?${qs}`);
  },

  // Import
  previewContasImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/import/contas/preview', fd);
  },
  confirmContasImport: (rows, overwrite) =>
    request('/import/contas/confirm', { method: 'POST', body: JSON.stringify({ rows, overwrite }) }),

  // Realizados — Serviços
  previewServicosImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/import/servicos/preview', fd);
  },
  confirmServicosImport: (rows, meses) =>
    request('/import/servicos/confirm', { method: 'POST', body: JSON.stringify({ rows, meses }) }),
  saveEsnMapping: (erp_name, esn_id) =>
    request('/import/servicos/esn-mapping', { method: 'POST', body: JSON.stringify({ erp_name, esn_id }) }),
  createInactiveEsn: (nome, erp_name) =>
    request('/import/servicos/esn-inactive', { method: 'POST', body: JSON.stringify({ nome, erp_name }) }),
  buscaCnpjLote: (lead_ids) =>
    request('/import/servicos/busca-cnpj', { method: 'POST', body: JSON.stringify({ lead_ids }) }),
  lookupCnpj: (cnpj) =>
    request(`/cnpj/${cnpj.replace(/\D/g,'')}`),
  confirmarCnpjLote: (confirmations) =>
    request('/import/servicos/confirmar-cnpj', { method: 'POST', body: JSON.stringify({ confirmations }) }),

  // Import de leads via e-mail do Totvs (Spotter/Protheus Workflow)
  previewTotvsEmailImport: (email_text) =>
    request('/import/totvs-email/preview', { method: 'POST', body: JSON.stringify({ email_text }) }),
  createTotvsEmailImport: (lead, raw_email, confirm_duplicate = false) =>
    request('/import/totvs-email', { method: 'POST', body: JSON.stringify({ lead, raw_email, confirm_duplicate }) }),

  previewEsnMetasImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return upload('/import/esn-metas/preview', fd);
  },
  confirmEsnMetasImport: (rows) =>
    request('/import/esn-metas/confirm', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Alertas
  getLeadAlertas: () => request('/leads/alertas'),

  // Interactions
  getInteractions: (leadId) => request(`/interactions?lead_id=${leadId}`),
  createInteraction: (leadId, data) => request('/interactions', { method: 'POST', body: JSON.stringify({ ...data, lead_id: leadId }) }),
  concluirInteraction: (_leadId, interactionId) => request(`/interactions/${interactionId}/concluir`, { method: 'PUT' }),
  deleteInteraction: (_leadId, interactionId) => request(`/interactions/${interactionId}`, { method: 'DELETE' }),
  sendEmailInteraction: (data) => request('/interactions/email', { method: 'POST', body: JSON.stringify(data) }),

  // Audit Log
  getAuditLog: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/audit-log?${qs}`);
  },
  getAuditLogUsers: () => request('/audit-log/users'),
  getLeadAudit: (leadId) => request(`/leads/${leadId}/audit`),

  // My Area
  getMyAreaSummary: () => request('/my-area/summary'),
  getMyAreaAcoesHoje: () => request('/my-area/acoes-hoje'),
  getMyAreaMetaResumo: (year) => request(`/my-area/meta-resumo?year=${year}`),
  getMyAreaLeadsAtencao: () => request('/my-area/leads-atencao'),

  // Notifications
  getNotifications: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/notifications?${qs}`);
  },
  getUnreadCount: () => request('/notifications/unread-count'),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: 'PUT' }),
  markAllNotificationsRead: () => request('/notifications/read-all', { method: 'PUT' }),

  // Agenda
  getAgenda: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/agenda?${qs}`);
  },
  concluirAgendaItem: (id) => request(`/agenda/${id}/concluir`, { method: 'PUT' }),

  // Reports
  getReport: (type, params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/${type}?${qs}`);
  },

  // Forecast Report (dedicated endpoint with summary + per-lead detail + XLSX export)
  getForecastReport: (params) => {
    const qs = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
    return request(`/reports/forecast?${qs}`);
  },
  downloadForecastReportXlsx: (params) => {
    const qs = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
    return download(`/reports/forecast/xlsx?${qs}`);
  },

  // WhatsApp templates
  getWhatsAppTemplates: () => request('/whatsapp-templates'),
  createWhatsAppTemplate: (data) => request('/whatsapp-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateWhatsAppTemplate: (id, data) => request(`/whatsapp-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWhatsAppTemplate: (id) => request(`/whatsapp-templates/${id}`, { method: 'DELETE' }),
  sendWhatsApp: (data) => request('/whatsapp-templates/send', { method: 'POST', body: JSON.stringify(data) }),
  // ZYN-59: status do provider WhatsApp
  getWhatsAppStatus: () => request('/whatsapp-templates/status'),

  // Automations
  getAutomations: () => request('/automations'),
  createAutomation: (data) => request('/automations', { method: 'POST', body: JSON.stringify(data) }),
  updateAutomation: (id, data) => request(`/automations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAutomation: (id) => request(`/automations/${id}`, { method: 'DELETE' }),
  toggleAutomation: (id) => request(`/automations/${id}/toggle`, { method: 'PUT' }),
  getAutomationExecutions: (id) => request(`/automations/${id}/executions`),

  // Proposals
  getProposalTemplates: () => request('/proposals/templates'),
  createProposalTemplate: (data) => request('/proposals/templates', { method: 'POST', body: JSON.stringify(data) }),
  getProposals: (leadId) => request(`/proposals?lead_id=${leadId}`),
  createProposal: (formData) => upload('/proposals', formData),
  updateProposal: (id, formData) => {
    const token = getToken();
    return fetch(`${API_BASE}/proposals/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error))));
  },
  updateProposalStatus: (id, status) => request(`/proposals/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  duplicateProposal: (id) => request(`/proposals/${id}/duplicate`, { method: 'POST' }),
  shareProposal: (id) => request(`/proposals/${id}/share`, { method: 'POST', body: JSON.stringify({}) }),

  // AI
  getAiSummary: (leadId, type = 'summary') => request(`/leads/${leadId}/ai-summary`, { method: 'POST', body: JSON.stringify({ type }) }),

  // Saved Reports
  getSavedReports: () => request('/reports/saved'),
  createSavedReport: (data) => request('/reports/saved', { method: 'POST', body: JSON.stringify(data) }),
  deleteSavedReport: (id) => request(`/reports/saved/${id}`, { method: 'DELETE' }),
  runCustomReport: (config) => request('/reports/custom', { method: 'POST', body: JSON.stringify(config) }),

  // Duplicates
  getDuplicates: () => request('/leads/duplicates'),
  mergeLeads: (data) => request('/leads/merge', { method: 'POST', body: JSON.stringify(data) }),

  // Webhooks
  getWebhooks: () => request('/webhooks'),
  createWebhook: (data) => request('/webhooks', { method: 'POST', body: JSON.stringify(data) }),
  updateWebhook: (id, data) => request(`/webhooks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWebhook: (id) => request(`/webhooks/${id}`, { method: 'DELETE' }),
  getWebhookLog: (id) => request(`/webhooks/${id}/log`),
  testWebhook: (id) => request(`/webhooks/${id}/test`, { method: 'POST' }),

  // Custom Fields
  getCustomFields: (entityType) => request(`/custom-fields?entity_type=${entityType}`),
  getAllCustomFields: (entityType) => request(`/custom-fields/all${entityType ? `?entity_type=${entityType}` : ''}`),
  createCustomField: (data) => request('/custom-fields', { method: 'POST', body: JSON.stringify(data) }),
  updateCustomField: (id, data) => request(`/custom-fields/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCustomField: (id) => request(`/custom-fields/${id}`, { method: 'DELETE' }),
  reorderCustomFields: (items) => request('/custom-fields/reorder', { method: 'PUT', body: JSON.stringify({ items }) }),
  getCustomFieldValues: (entityId, entityType) => request(`/custom-fields/values?entity_id=${entityId}&entity_type=${entityType}`),
  saveCustomFieldValues: (items) => request('/custom-fields/values', { method: 'PUT', body: JSON.stringify({ items }) }),

  // Search
  search: (q, types = 'leads,contas,users,products') =>
    request(`/search?q=${encodeURIComponent(q)}&types=${types}&limit=8`),

  // Cross-sell
  getCrossSellOpportunities: (params) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/cross-sell/opportunities?${qs}`);
  },
  getCrossSellSummary: () => request('/cross-sell/summary'),

  // Playbooks
  getPlaybooks: () => request('/playbooks'),
  createPlaybook: (data) => request('/playbooks', { method: 'POST', body: JSON.stringify(data) }),
  updatePlaybook: (id, data) => request(`/playbooks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePlaybook: (id) => request(`/playbooks/${id}`, { method: 'DELETE' }),
  addPlaybookStep: (id, data) => request(`/playbooks/${id}/steps`, { method: 'POST', body: JSON.stringify(data) }),
  deletePlaybookStep: (stepId) => request(`/playbooks/steps/${stepId}`, { method: 'DELETE' }),
  applyPlaybook: (id, leadId) => request(`/playbooks/${id}/apply`, { method: 'POST', body: JSON.stringify({ lead_id: leadId }) }),
  getLeadPlaybooks: (leadId) => request(`/playbooks/lead/${leadId}`),
  cancelPlaybookInstance: (instanceId) => request(`/playbooks/instances/${instanceId}`, { method: 'DELETE' }),

  // Generic helper — for ad-hoc GET requests
  get: (path) => request(path.replace(/^\/api/, '')),

  // Users Management
  getUsers: () => request('/users'),
  getUserRoles: () => request('/users/roles'),
  getUserRolePermissions: (roleId) => request(`/users/roles/${roleId}/permissions`),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  linkEsnsAuto: () => request('/users/link-esns-auto', { method: 'POST', body: JSON.stringify({}) }),

  // Feature flags
  getFeatureFlag: (key) => request(`/admin/feature-flags/${key}`),
  getFeatureFlags: () => request('/admin/feature-flags'),
  updateFeatureFlag: (key, data) => request(`/admin/feature-flags/${key}`, { method: 'PUT', body: JSON.stringify(data) }),

  // ZYN-326: menu_config — overrides cosméticos da Sidebar
  getMenuConfig: () => request('/menu-config'),
  updateMenuConfig: (items) => request('/admin/menu-config', { method: 'PUT', body: JSON.stringify(items) }),
  resetMenuConfig: () => request('/admin/menu-config', { method: 'DELETE' }),

  // ZYN-189: Agent API Keys
  getAgentApiKeys: () => request('/admin/keys'),
  createAgentApiKey: (data) => request('/admin/keys', { method: 'POST', body: JSON.stringify(data) }),
  toggleAgentApiKey: (id) => request(`/admin/keys/${id}/toggle`, { method: 'POST' }),
  deleteAgentApiKey: (id) => request(`/admin/keys/${id}`, { method: 'DELETE' }),

  // APNs — gerenciamento (admin)
  getApns: () => request('/apns'),
  createApn: (data) => request('/apns', { method: 'POST', body: JSON.stringify(data) }),
  updateApn: (id, data) => request(`/apns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // APN Workspace
  getApnProspects: () => request('/apns/prospects'),
  createApnProspect: (data) => request('/apns/prospects', { method: 'POST', body: JSON.stringify(data) }),
  apnHandoff: (id, data) => request(`/apns/prospects/${id}/handoff`, { method: 'POST', body: JSON.stringify(data) }),
  getApnMetrics: () => request('/apns/metrics'),

  // Google Calendar integration
  getGcalConfig: () => request('/users/me/gcal-config'),
  saveGcalConfig: (gcal_zapier_url) =>
    request('/users/me/gcal-config', { method: 'POST', body: JSON.stringify({ gcal_zapier_url }) }),
  testGcalWebhook: () => request('/users/me/gcal-test', { method: 'POST' }),

  // ─── Admin Notifications Panel ───
  getNotifRules: () => request('/admin/notifications/rules'),
  getNotifRule: (type) => request(`/admin/notifications/rules/${type}`),
  updateNotifRule: (type, patch) =>
    request(`/admin/notifications/rules/${type}`, { method: 'PUT', body: JSON.stringify(patch) }),
  createNotifRule: (data) =>
    request('/admin/notifications/rules', { method: 'POST', body: JSON.stringify(data) }),
  deleteNotifRule: (type) =>
    request(`/admin/notifications/rules/${type}`, { method: 'DELETE' }),
  testNotifRule: (type, payload = {}) =>
    request(`/admin/notifications/rules/${type}/test`, { method: 'POST', body: JSON.stringify(payload) }),

  getNotifChannels: () => request('/admin/notifications/channels'),
  testNotifChannel: (name) =>
    request(`/admin/notifications/channels/${name}/test`, { method: 'POST', body: JSON.stringify({}) }),

  getNotifUsers: () => request('/admin/notifications/users'),
  getNotifUserPrefs: (userId) => request(`/admin/notifications/users/${userId}/prefs`),
  saveNotifUserPref: (userId, ruleType, patch) =>
    request(`/admin/notifications/users/${userId}/prefs/${ruleType}`, { method: 'PUT', body: JSON.stringify(patch) }),

  getNotifDispatches: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')).toString();
    return request(`/admin/notifications/dispatches${qs ? '?' + qs : ''}`);
  },
  retryNotifDispatch: (id) =>
    request(`/admin/notifications/dispatches/${id}/retry`, { method: 'POST', body: JSON.stringify({}) }),

  getNotifMetrics: () => request('/admin/notifications/metrics'),

  // ─── Telegram QR Linking ───
  getTelegramInfo: () => request('/users/me/telegram-info'),
  requestTelegramLink: () => request('/users/me/telegram-link', { method: 'POST', body: JSON.stringify({}) }),
  checkTelegramLink: (token) => request(`/users/me/telegram-link/${token}/status`),
  unlinkTelegram: () => request('/users/me/telegram-unlink', { method: 'POST', body: JSON.stringify({}) }),

  // ─── Telegram Admin Config ───
  getAdminTelegramConfig: () => request('/users/admin/telegram-config'),
  saveAdminTelegramConfig: (bot_token) =>
    request('/users/admin/telegram-config', { method: 'PUT', body: JSON.stringify({ bot_token }) }),

  // ─── Profile: atualizar e-mail ───
  updateMyEmail: (email) =>
    request('/users/me/email', { method: 'PUT', body: JSON.stringify({ email }) }),

  // ─── Conciliação de Forecast ───
  getReconciliacaoPending: () => request('/reconciliacao/pending'),
  getReconciliacaoItems: () => request('/reconciliacao/items'),
  resolveReconciliacaoItem: (id, body) =>
    request(`/reconciliacao/items/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  triggerReconciliacao: (mes) =>
    request('/reconciliacao/trigger', { method: 'POST', body: JSON.stringify({ mes }) }),
  getAdminReconciliacao: () => request('/reconciliacao/admin'),
  batchResolveReconciliacao: (id, esn_id) =>
    request(`/reconciliacao/admin/${id}/batch-resolve`, { method: 'POST', body: JSON.stringify({ esn_id }) }),
  criarLote: (mes) =>
    request('/reconciliacao/lote', { method: 'POST', body: JSON.stringify({ mes }) }),
  resolveItem: (id, body) =>
    request(`/reconciliacao/items/${id}/resolve`, { method: 'POST', body: JSON.stringify(body) }),
  postergarTodos: () =>
    request('/reconciliacao/postergar-todos', { method: 'POST', body: JSON.stringify({}) }),
  getProgresso: () =>
    request('/reconciliacao/progresso'),

  // ─── ZYN-50: Rotting Config ───
  getRottingConfig: () => request('/admin/rotting-config'),
  updateRottingConfig: (funnel_type, body) =>
    request(`/admin/rotting-config/${funnel_type}`, { method: 'PUT', body: JSON.stringify(body) }),

  // ─── ZYN-51: Required Fields Config ───
  getRequiredFieldsConfig: () => request('/admin/required-fields'),
  saveRequiredFieldsConfig: (config) =>
    request('/admin/required-fields', { method: 'PUT', body: JSON.stringify(config) }),

  // ─── ZYN-53: Meta Progress Widget ───
  getMyProgress: () => request('/esn-targets/my-progress'),

  // ─── ZYN-55: Forecast Categories ───
  getForecastCategoriesSummary: () => request('/forecast/categories-summary'),

  // ─── ZYN-58: Relatório de Atividade ───
  getActivityReport: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/activity${qs ? '?' + qs : ''}`);
  },

  // ─── ZYN-60: Tasks ───
  getTasks: (lead_id) => request(`/tasks?lead_id=${lead_id}`),
  createTask: (body) => request('/tasks', { method: 'POST', body: JSON.stringify(body) }),
  completeTask: (id, body) => request(`/tasks/${id}/complete`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTask: (id) => request(`/tasks/${id}`, { method: 'DELETE' }),

  // ─── ZYN-61: Win/Loss por Produto e ESN ───
  getWinLossByProduct: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/win-loss/by-product${qs ? '?' + qs : ''}`);
  },
  getWinLossFunnelLeak: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/win-loss/funnel-leak${qs ? '?' + qs : ''}`);
  },
  getWinLossByEsn: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/win-loss/by-esn${qs ? '?' + qs : ''}`);
  },
  getWinLossEsnXMotivo: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/reports/win-loss/esn-x-motivo${qs ? '?' + qs : ''}`);
  },

  // ─── ZYN-57: Email Tracking stats ───
  getEmailTrackingStats: (leadId) => request(`/interactions/email-tracking/${leadId}`),

  // ─── ZYN-63: Contexto de Conta no Lead ───
  getContaContext: (contaId, excludeLeadId) =>
    request(`/contas/${contaId}/context?exclude_lead=${excludeLeadId || 0}`),

  // ─── ZYN-65: Forecast Projection + Settings ───
  getForecastProjection: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/forecast/projection${qs ? '?' + qs : ''}`);
  },
  getForecastSettings: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/forecast/settings${qs ? '?' + qs : ''}`);
  },
  saveForecastSettings: (body) =>
    request('/forecast/settings', { method: 'PUT', body: JSON.stringify(body) }),

  // ─── ZYN-62: Assinatura Digital ───
  requestProposalSignature: (id, body) =>
    request(`/proposals/${id}/request-signature`, { method: 'POST', body: JSON.stringify(body) }),
  signProposal: (token, body) =>
    request(`/proposals/portal/${token}/sign`, { method: 'POST', body: JSON.stringify(body) }),

  // ─── ZYN-64: PWA Push Notifications ───
  getVapidPublicKey: () => request('/notifications/vapid-public-key'),
  subscribePush: (subscription) =>
    request('/notifications/push-subscribe', { method: 'POST', body: JSON.stringify(subscription) }),
  unsubscribePush: (endpoint) =>
    request('/notifications/push-subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),

  // ─── ZYN-67: Email Templates ───
  getEmailTemplates: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/email-templates${qs ? '?' + qs : ''}`);
  },
  getEmailTemplate: (id) => request(`/email-templates/${id}`),
  createEmailTemplate: (body) =>
    request('/email-templates', { method: 'POST', body: JSON.stringify(body) }),
  updateEmailTemplate: (id, body) =>
    request(`/email-templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEmailTemplate: (id) =>
    request(`/email-templates/${id}`, { method: 'DELETE' }),

  // ─── ZYN-69: Onboarding ───
  getOnboardingStatus: () => request('/users/me/onboarding-status'),
  completeOnboarding: () => request('/users/me/onboarding-complete', { method: 'PUT' }),

  // ─── ZYN-68: Gamificação ───
  getMyBadges: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/gamification/my-badges${qs ? '?' + qs : ''}`);
  },
  getEsnBadges: (esnId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/gamification/badges/${esnId}${qs ? '?' + qs : ''}`);
  },
  getGamificationRanking: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/gamification/ranking${qs ? '?' + qs : ''}`);
  },
  getGamificationLeaderboard: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/gamification/leaderboard${qs ? '?' + qs : ''}`);
  },

  // ─── ZYN-70: ESN Performance (Design System) ───
  getEsnReport: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/dashboard/esn-ranking${qs ? '?' + qs : ''}`);
  },

  // ─── Portfolio Transfer (Admin) ───
  getOrphanRecords: (detail = false) =>
    request(`/admin/portfolio/orphan-records${detail ? '?detail=1' : ''}`),
  transferPortfolio: (leads, contas) =>
    request('/admin/portfolio/transfer-portfolio', {
      method: 'POST',
      body: JSON.stringify({ leads, contas }),
    }),

  // ─── Version info ───
  getVersion: () => request('/version'),
};
